import type { ClientLocationPlan, ClientLocationPlanListData } from "api-types/locationPlans/locationPlansResponses";
import {
  evaluateHomePlanCreationEligibility,
  type HomePlanCreationBlockReason,
  type HomePlanCreationEligibility,
} from "api-types/locations/homePlanCreationEligibility";
import type { ClientLocationWithLevel } from "api-types/locations/locationBaseTypes";
import { isMultiUnitLocation } from "api-types/locations/locationDisplay";
import type { ClientMasterPlan } from "api-types/masterPlans/masterPlansResponses";
import { isMultiUnitPlan } from "lib/components/builders/communities/plans/planUtils";
import {
  MASTER_PLAN_UNRESOLVED_HOME_PLAN_MESSAGE,
  MULTI_UNIT_PLAN_AT_NON_MULTI_UNIT_LOCATION,
} from "lib/constants/messages";

export type { HomePlanCreationBlockReason, HomePlanCreationEligibility };

// Block reasons, client-side adds `no_location_plans` and `master_plan_unresolved`.
// The server evaluator only checks a single plan/location, so it never produces these reasons.
export type ClientHomePlanCreationBlockReason =
  | HomePlanCreationBlockReason
  | "no_location_plans"
  | "master_plan_unresolved";
export type ClientHomePlanCreationEligibility = { ok: true } | { ok: false; reason: ClientHomePlanCreationBlockReason };

// Reasons that surface a tooltip / inline message. `no_location_plans` intentionally omitted.
export const HOME_PLAN_CREATION_BLOCK_MESSAGES: Partial<Record<ClientHomePlanCreationBlockReason, string>> = {
  multi_unit_plan_at_single_unit_location: MULTI_UNIT_PLAN_AT_NON_MULTI_UNIT_LOCATION,
  master_plan_unresolved: MASTER_PLAN_UNRESOLVED_HOME_PLAN_MESSAGE,
};

export function evaluateHomePlanCreationForLocationPlan({
  location,
  locationPlan,
  masterPlans,
}: {
  location: ClientLocationWithLevel;
  locationPlan: ClientLocationPlan;
  masterPlans: ClientMasterPlan[];
}): ClientHomePlanCreationEligibility {
  const planIsMultiUnit = isMultiUnitPlan(locationPlan, masterPlans);
  if (planIsMultiUnit === undefined) {
    return { ok: false, reason: "master_plan_unresolved" };
  }
  return evaluateHomePlanCreationEligibility({
    atMultiUnitLocation: isMultiUnitLocation(location),
    planIsMultiUnit,
  });
}

export function partitionCreatableLocationPlans({
  location,
  locationPlans,
  masterPlans,
}: {
  location: ClientLocationWithLevel;
  locationPlans: ClientLocationPlan[];
  masterPlans: ClientMasterPlan[];
}): {
  creatable: ClientLocationPlan[];
  blocked: Array<{ plan: ClientLocationPlan; reason: ClientHomePlanCreationBlockReason }>;
} {
  const creatable: ClientLocationPlan[] = [];
  const blocked: Array<{ plan: ClientLocationPlan; reason: ClientHomePlanCreationBlockReason }> = [];

  for (const plan of locationPlans) {
    const eligibility = evaluateHomePlanCreationForLocationPlan({ location, locationPlan: plan, masterPlans });
    if (eligibility.ok) {
      creatable.push(plan);
    } else {
      blocked.push({ plan, reason: eligibility.reason });
    }
  }

  return { creatable, blocked };
}

export function evaluateHomePlanCreationForLocation({
  location,
  locationPlans,
  masterPlans,
}: {
  location: ClientLocationWithLevel;
  locationPlans: ClientLocationPlan[];
  masterPlans: ClientMasterPlan[];
}): ClientHomePlanCreationEligibility {
  const { creatable, blocked } = partitionCreatableLocationPlans({ location, locationPlans, masterPlans });
  if (creatable.length > 0) {
    return { ok: true };
  }
  if (blocked.length > 0) {
    return { ok: false, reason: blocked[0].reason };
  }
  // No plans at the location — Add Home Plan stays disabled without the MU tooltip.
  return { ok: false, reason: "no_location_plans" };
}

// Add Home Plan eligibility from a location's plan-list payload. While the payload is
// missing or still loading, returns `no_location_plans` so create stays disabled.
export function getLocationCreateEligibility(
  locationPlansData: ClientLocationPlanListData | undefined,
): ClientHomePlanCreationEligibility {
  if (!locationPlansData?.location || !locationPlansData.locationPlans) {
    return { ok: false, reason: "no_location_plans" };
  }
  return evaluateHomePlanCreationForLocation({
    location: locationPlansData.location,
    locationPlans: locationPlansData.locationPlans,
    masterPlans: locationPlansData.masterPlans ?? [],
  });
}

export function getHomePlanCreationBlockMessage(
  eligibility: HomePlanCreationEligibility | ClientHomePlanCreationEligibility,
): string | undefined {
  if (eligibility.ok) {
    return undefined;
  }
  return HOME_PLAN_CREATION_BLOCK_MESSAGES[eligibility.reason];
}
